import React, { createContext, useContext, useState } from 'react';

const TabsContext = createContext({
    value: undefined,
    onValueChange: () => { },
});

const listStyle = {
    display: 'inline-flex',
    height: '2.5rem',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.375rem',
    backgroundColor: '#f1f5f9', // slate-100
    padding: '0.25rem',
    color: '#64748b' // slate-500
};

const triggerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    borderRadius: '0.125rem',
    padding: '0.375rem 0.75rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '1.25rem',
    transition: 'all 0.2s',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: 'transparent', // default
    color: 'inherit'
};

const activeTriggerStyle = {
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    color: '#0f172a' // slate-950
};

export const Tabs = ({ defaultValue, value, onValueChange, className, children, style, ...props }) => {
    const [internalValue, setInternalValue] = useState(defaultValue);

    const handleValueChange = (newValue) => {
        if (value !== undefined) {
            onValueChange?.(newValue);
        } else {
            setInternalValue(newValue);
        }
    };

    const activeValue = value !== undefined ? value : internalValue;

    return (
        <TabsContext.Provider value={{ value: activeValue, onValueChange: handleValueChange }}>
            <div
                style={{ ...style }} // Basic container
                {...props}
            >
                {children}
            </div>
        </TabsContext.Provider>
    );
};

export const TabsList = ({ className, children, style, ...props }) => {
    return (
        <div
            style={{ ...listStyle, ...style }}
            {...props}
        >
            {children}
        </div>
    );
};

export const TabsTrigger = ({ className, value, children, style, ...props }) => {
    const { value: selectedValue, onValueChange } = useContext(TabsContext);
    const isActive = selectedValue === value;

    return (
        <button
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onValueChange(value)}
            style={{
                ...triggerStyle,
                ...(isActive ? activeTriggerStyle : {}),
                ...style
            }}
            {...props}
        >
            {children}
        </button>
    );
};

export const TabsContent = ({ className, value, children, style, ...props }) => {
    const { value: selectedValue } = useContext(TabsContext);

    if (selectedValue !== value) return null;

    return (
        <div
            role="tabpanel"
            style={{
                marginTop: '0.5rem',
                outline: 'none',
                ...style
            }}
            {...props}
        >
            {children}
        </div>
    );
};
